import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Star, Calendar, Users, ChevronRight, Facebook, Instagram, Youtube, Stethoscope, Eye, UserCheck, LogOut } from 'lucide-react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import BabiDocLogo from '../components/BabiDocLogo';

// URL du logo officiel BabiDoc
const LOGO_URL = "https://customer-assets.emergentagent.com/job_babidoc-health/artifacts/7a0rukid_HUMMM0fc84adc-81cc-4751-9a5e-2c9327247823.png";

// Contact email
const CONTACT_EMAIL = "babidoc.net@gmail.com";

// Liste des villes disponibles (par ordre alphabétique)
const CITIES = [
  "Abobo",
  "Adjamé",
  "Attécoubé",
  "Cocody",
  "Koumassi",
  "Marcory",
  "Plateau",
  "Port-Bouet",
  "Treichville",
  "Yopougon"
];

// Liste des spécialités médicales courantes
const SPECIALTIES = [
  "Allergologie",
  "Anesthésiologie",
  "Cardiologie",
  "Chirurgie Générale",
  "Chirurgie Orthopédique",
  "Chirurgie Plastique",
  "Dermatologie",
  "Endocrinologie",
  "Gastro-entérologie",
  "Gériatrie",
  "Gynécologie",
  "Hématologie",
  "Infectiologie",
  "Médecine Générale",
  "Médecine Interne",
  "Néphrologie",
  "Neurologie",
  "Oncologie",
  "Ophtalmologie",
  "ORL (Oto-Rhino-Laryngologie)",
  "Pédiatrie",
  "Pneumologie",
  "Psychiatrie",
  "Radiologie",
  "Rhumatologie",
  "Urologie"
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [searchCity, setSearchCity] = useState('');
  const [searchSpecialty, setSearchSpecialty] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState('');
  const [stats, setStats] = useState({ visits: 0, connections: 0 });

  // Get user initials
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Navigate to dashboard based on role
  const goToDashboard = () => {
    if (user?.role === 'doctor') {
      navigate('/doctor-dashboard');
    } else if (user?.role === 'admin') {
      navigate('/admin-dashboard');
    } else {
      navigate('/patient-dashboard');
    }
  };

  // Fetch stats and record visit on page load
  useEffect(() => {
    const fetchStatsAndRecordVisit = async () => {
      try {
        // Record this visit
        await axios.post(`${API}/stats/visit`);
        
        // Fetch updated stats
        const response = await axios.get(`${API}/stats/public`);
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats', error);
      }
    };
    
    fetchStatsAndRecordVisit();
  }, []);

  const searchDoctors = async () => {
    if (!searchCity && !searchSpecialty) return;
    setLoading(true);
    try {
      let url = `${API}/doctors?`;
      if (searchCity && searchCity !== 'all') {
        url += `city=${encodeURIComponent(searchCity)}&`;
      }
      if (searchSpecialty && searchSpecialty !== 'all') {
        url += `specialization=${encodeURIComponent(searchSpecialty)}`;
      }
      const response = await axios.get(url);
      setDoctors(response.data);
    } catch (error) {
      console.error('Failed to search doctors', error);
    } finally {
      setLoading(false);
    }
  };

  // Géolocalisation - trouver les médecins à proximité
  const findNearbyDoctors = async () => {
    setGeoLoading(true);
    setGeoError('');
    
    if (!navigator.geolocation) {
      setGeoError('La géolocalisation n\'est pas supportée par votre navigateur');
      setGeoLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await axios.get(`${API}/doctors/nearby?lat=${latitude}&lng=${longitude}&radius=10`);
          setDoctors(response.data);
          
          // Scroll to results
          setTimeout(() => {
            document.getElementById('search-results')?.scrollIntoView({ behavior: 'smooth' });
          }, 300);
        } catch (error) {
          console.error('Failed to find nearby doctors', error);
          // Fallback: show all doctors
          try {
            const response = await axios.get(`${API}/doctors`);
            setDoctors(response.data);
          } catch (e) {
            setGeoError('Erreur lors de la recherche');
          }
        } finally {
          setGeoLoading(false);
        }
      },
      (error) => {
        setGeoLoading(false);
        switch(error.code) {
          case error.PERMISSION_DENIED:
            setGeoError('Veuillez autoriser la géolocalisation dans votre navigateur');
            break;
          case error.POSITION_UNAVAILABLE:
            setGeoError('Position non disponible');
            break;
          case error.TIMEOUT:
            setGeoError('La demande de géolocalisation a expiré');
            break;
          default:
            setGeoError('Erreur de géolocalisation');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  return (
    <div className="min-h-screen overflow-x-hidden w-full">
      {/* Stats Counter - Fixed top right */}
      <div className="fixed top-16 sm:top-24 right-2 sm:right-4 z-40 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-2 sm:p-3 border border-gray-200" data-testid="stats-counter">
        <div className="flex flex-col space-y-1 text-xs sm:text-sm">
          <div className="flex items-center space-x-1.5 text-teal-700">
            <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="font-semibold">{stats.visits.toLocaleString()}</span>
            <span className="text-gray-500 hidden sm:inline">visites</span>
          </div>
          <div className="flex items-center space-x-1.5 text-orange-600">
            <UserCheck className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="font-semibold">{stats.connections.toLocaleString()}</span>
            <span className="text-gray-500 hidden sm:inline">connexions</span>
          </div>
        </div>
      </div>

      {/* Navigation - Mobile optimized */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-20">
            <div className="flex items-center space-x-1.5 sm:space-x-2 flex-shrink-0">
              <img 
                src={LOGO_URL} 
                alt="Côte d'Ivoire" 
                className="w-7 h-7 sm:w-10 sm:h-10 object-cover flex-shrink-0 rounded-full"
              />
              <h1 className="text-sm sm:text-2xl font-bold text-teal-700 whitespace-nowrap">BabiDoc</h1>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-4 flex-shrink-0">
              {user ? (
                /* User is logged in - Show avatar with initials */
                <div className="flex items-center gap-2 sm:gap-3">
                  <button
                    onClick={goToDashboard}
                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                    data-testid="user-avatar-btn"
                  >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-teal-500 to-green-600 flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-lg">
                      {getInitials(user.full_name)}
                    </div>
                    <span className="hidden sm:block text-sm font-medium text-gray-700 max-w-[120px] truncate">
                      {user.full_name}
                    </span>
                  </button>
                  <Button 
                    variant="ghost" 
                    onClick={handleLogout}
                    className="text-[11px] sm:text-base px-1.5 sm:px-3 h-7 sm:h-10 text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Déconnexion</span>
                  </Button>
                </div>
              ) : (
                /* User is not logged in - Show login/register buttons */
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/login')} 
                    data-testid="nav-login-btn" 
                    className="text-[11px] sm:text-base px-1.5 sm:px-4 h-7 sm:h-10 whitespace-nowrap min-w-0"
                  >
                    Connexion
                  </Button>
                  <Button 
                    className="btn-primary text-[11px] sm:text-base px-2 sm:px-4 h-7 sm:h-10 whitespace-nowrap min-w-0" 
                    onClick={() => navigate('/register')} 
                    data-testid="nav-register-btn"
                  >
                    Inscription
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Mobile optimized */}
      <section className="pt-16 sm:pt-32 pb-8 sm:pb-20 px-3 sm:px-6 lg:px-8 relative overflow-hidden bg-gradient-to-br from-orange-600 via-orange-500 to-green-700">
        <div className="absolute inset-0 -z-10 opacity-30">
          <div className="absolute top-0 right-0 w-32 h-32 sm:w-96 sm:h-96 bg-green-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 sm:w-96 sm:h-96 bg-orange-700 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-center">
            {/* Text content */}
            <div className="animate-fade-in order-2 lg:order-1">
              <h1 className="text-xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-3 sm:mb-6 leading-tight drop-shadow-lg">
                Trouvez Votre <span className="text-white">Professionnel de Santé</span> de Confiance
              </h1>
              <p className="text-xs sm:text-base lg:text-lg text-white/95 mb-4 sm:mb-8 leading-relaxed drop-shadow">
                Connectez-vous avec des médecins qualifiés près de chez vous. Prenez rendez-vous instantanément et obtenez les soins que vous méritez auprès des meilleurs professionnels médicaux d'Abidjan.
              </p>
              
              {/* Search Box */}
              <div className="glass p-3 sm:p-6 rounded-xl sm:rounded-2xl shadow-xl" data-testid="search-section">
                <div className="flex flex-col gap-2 sm:gap-4">
                  {/* City and Specialty selectors */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                    {/* City Select */}
                    <div className="relative">
                      <Select value={searchCity} onValueChange={setSearchCity}>
                        <SelectTrigger className="h-10 sm:h-14 text-sm sm:text-base pl-9 sm:pl-12" data-testid="city-select">
                          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                          <SelectValue placeholder="Choisir une ville" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Toutes les villes</SelectItem>
                          {CITIES.map((city) => (
                            <SelectItem key={city} value={city}>{city}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Specialty Select */}
                    <div className="relative">
                      <Select value={searchSpecialty} onValueChange={setSearchSpecialty}>
                        <SelectTrigger className="h-10 sm:h-14 text-sm sm:text-base pl-9 sm:pl-12" data-testid="specialty-select">
                          <Stethoscope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                          <SelectValue placeholder="Choisir une spécialité" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Toutes les spécialités</SelectItem>
                          {SPECIALTIES.map((specialty) => (
                            <SelectItem key={specialty} value={specialty}>{specialty}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Search Button */}
                  <Button
                    onClick={searchDoctors}
                    disabled={loading || (!searchCity && !searchSpecialty)}
                    className="btn-primary h-10 sm:h-14 px-4 sm:px-8 space-x-2 text-sm sm:text-base w-full"
                    data-testid="search-btn"
                  >
                    <Search className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>{loading ? 'Recherche...' : 'Rechercher un médecin'}</span>
                  </Button>
                </div>
              </div>
            </div>

            {/* Hero Image - Mobile optimized */}
            <div className="relative animate-fade-in order-1 lg:order-2">
              <div className="relative rounded-xl sm:rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_medical-connect-1/artifacts/b0fw6y5q_image.png"
                  alt="Médecin africain"
                  className="w-full h-[180px] sm:h-[350px] lg:h-[500px] object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search Results */}
      {doctors.length > 0 && (
        <section className="py-8 sm:py-12 px-3 sm:px-6 lg:px-8 bg-gray-50" data-testid="search-results">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8">Médecins Disponibles</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {doctors.map((doctor) => (
                <Card key={doctor.id} className="card cursor-pointer" onClick={() => navigate(`/doctor/${doctor.id}`)} data-testid={`doctor-card-${doctor.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-xl mb-1">{doctor.doctor_name || 'Dr.'}</CardTitle>
                        <p className="text-base text-gray-700 font-medium mb-1">{doctor.clinic_name}</p>
                        <p className="text-sm text-teal-600 font-medium">{doctor.specialization || 'Médecin'}</p>
                      </div>
                      {doctor.rating > 0 && (
                        <div className="flex items-center space-x-1 badge">
                          <Star className="w-4 h-4 fill-current" />
                          <span>{doctor.rating}</span>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-4 h-4" />
                        <span>{doctor.city}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Users className="w-4 h-4" />
                        <span>{doctor.experience_years} ans d'expérience</span>
                      </div>
                      
                      {/* Biography */}
                      {doctor.bio && (
                        <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-600 italic line-clamp-2">{doctor.bio}</p>
                        </div>
                      )}
                      
                      <div className="mt-4 pt-4 border-t flex items-center justify-between">
                        <span className="font-semibold text-gray-900">{doctor.consultation_fee.toLocaleString()} FCFA</span>
                        <ChevronRight className="w-5 h-5 text-teal-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-12 sm:py-20 px-3 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Pourquoi Choisir BabiDoc?</h2>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 px-4">Votre santé est notre priorité</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Geolocation Card - Clickable */}
            <div 
              className="card text-center cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 border-transparent hover:border-teal-500"
              onClick={findNearbyDoctors}
              data-testid="find-nearby-btn"
            >
              <div className="w-16 h-16 rounded-full gradient-primary flex items-center justify-center mx-auto mb-6">
                {geoLoading ? (
                  <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <MapPin className="w-8 h-8 text-white" />
                )}
              </div>
              <h3 className="text-xl font-bold mb-3">
                {geoLoading ? 'Recherche en cours...' : 'Trouvez des Médecins à Proximité'}
              </h3>
              <p className="text-gray-600">
                {geoLoading 
                  ? 'Localisation de votre position...' 
                  : 'Cliquez ici pour localiser les médecins près de chez vous'}
              </p>
              {geoError && (
                <p className="text-red-500 text-sm mt-2">{geoError}</p>
              )}
            </div>
            
            {/* Instant Booking Card - Clickable */}
            <div 
              className="card text-center cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 border-transparent hover:border-orange-500"
              onClick={() => navigate('/patient-dashboard')}
              data-testid="instant-booking-btn"
            >
              <div className="w-16 h-16 rounded-full gradient-secondary flex items-center justify-center mx-auto mb-6">
                <Calendar className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Réservation Instantanée</h3>
              <p className="text-gray-600">Cliquez ici pour voir les médecins disponibles et prendre rendez-vous en quelques clics.</p>
            </div>
            
            {/* Verified Reviews Card - Clickable */}
            <div 
              className="card text-center cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 border-transparent hover:border-yellow-500"
              onClick={() => navigate('/feedback')}
              data-testid="reviews-btn"
            >
              <div className="w-16 h-16 rounded-full gradient-primary flex items-center justify-center mx-auto mb-6">
                <Star className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Avis Vérifiés</h3>
              <p className="text-gray-600">Cliquez ici pour partager votre expérience et nous aider à améliorer nos services.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20 px-3 sm:px-6 lg:px-8 bg-gradient-to-br from-green-700 via-green-600 to-orange-500 relative overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-20">
          <div className="absolute top-0 right-0 w-48 h-48 sm:w-96 sm:h-96 bg-orange-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 sm:w-96 sm:h-96 bg-green-800 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto text-center text-white relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 px-4">Rejoignez Babidoc</h2>
          <p className="text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 opacity-90 px-4">
            Rejoignez BabiDoc et touchez des milliers de patients à la recherche de services de santé de qualité.
          </p>
          <Button
            onClick={() => navigate('/register')}
            className="bg-white text-teal-700 hover:bg-gray-100 h-12 sm:h-14 px-6 sm:px-10 text-base sm:text-lg font-semibold rounded-full"
            data-testid="cta-register-btn"
          >
            Inscrivez-vous Maintenant
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 sm:py-12 px-3 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Logo and tagline */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="flex items-center justify-center space-x-2 mb-3 sm:mb-4">
              <img 
                src={LOGO_URL} 
                alt="Côte d'Ivoire" 
                className="w-10 h-10 object-cover rounded-full"
              />
              <h3 className="text-xl sm:text-2xl font-bold">BabiDoc</h3>
            </div>
            <p className="text-sm sm:text-base text-gray-400 mb-2">Des Soins de Santé de Qualité à Portée de Main</p>
          </div>

          {/* Social Links */}
          <div className="flex items-center justify-center space-x-6 mb-6 sm:mb-8">
            {/* Facebook */}
            <a 
              href="https://www.facebook.com/profile.php?id=61588099809786" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors duration-300"
              aria-label="Facebook"
              data-testid="social-facebook"
            >
              <Facebook className="w-5 h-5 sm:w-6 sm:h-6" />
            </a>

            {/* Instagram */}
            <a 
              href="https://www.instagram.com/babidoc.net2025/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-800 hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-500 rounded-full flex items-center justify-center transition-colors duration-300"
              aria-label="Instagram"
              data-testid="social-instagram"
            >
              <Instagram className="w-5 h-5 sm:w-6 sm:h-6" />
            </a>

            {/* YouTube */}
            <a 
              href="https://youtube.com/@babidoc" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-800 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors duration-300"
              aria-label="YouTube"
              data-testid="social-youtube"
            >
              <Youtube className="w-5 h-5 sm:w-6 sm:h-6" />
            </a>

            {/* TikTok */}
            <a 
              href="https://www.tiktok.com/@babidoc.net" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-800 hover:bg-black rounded-full flex items-center justify-center transition-colors duration-300"
              aria-label="TikTok"
              data-testid="social-tiktok"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </a>
          </div>

          {/* Medical Centers Directory */}
          <div className="text-center mb-6 sm:mb-8 p-4 bg-gray-800 rounded-lg mx-4 sm:mx-auto sm:max-w-xl">
            <div className="space-y-4">
              {/* Centres médicaux et cliniques */}
              <a 
                href="https://customer-assets.emergentagent.com/job_docfinder-79/artifacts/6gpc1lsl_CENTRES-MEDICAUX-ET-CLINIQUES-PARTENAIRES-ZONE-ABIDJAN-ALLIANZ-CI.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-3 text-teal-400 hover:text-teal-300 transition-colors"
                data-testid="medical-centers-link"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-sm sm:text-base font-medium">
                  🏥 Centres Médicaux et Cliniques d'Abidjan
                </span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </a>
              
              {/* Médecins traitants */}
              <a 
                href="https://customer-assets.emergentagent.com/job_docfinder-79/artifacts/jx47v4ch_MEDECINS-TRAITANTS-ZONE-ABIDJAN-2018-17-12-2018.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-3 text-teal-400 hover:text-teal-300 transition-colors"
                data-testid="doctors-list-link"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-sm sm:text-base font-medium">
                  👨‍⚕️ Médecins Traitants d'Abidjan
                </span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </a>
            </div>
            <p className="text-xs text-gray-500 mt-3">Télécharger les annuaires médicaux d'Abidjan</p>
          </div>

          {/* Contact info */}
          <div className="text-center mb-4 sm:mb-6">
            <p className="text-xs sm:text-sm text-gray-400">
              Contactez-nous: <a href={`mailto:${CONTACT_EMAIL}`} className="text-teal-400 hover:text-teal-300 underline">{CONTACT_EMAIL}</a>
            </p>
          </div>

          {/* Copyright */}
          <div className="text-center border-t border-gray-800 pt-4 sm:pt-6">
            <p className="text-xs sm:text-sm text-gray-500">© 2025 BabiDoc. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;